/* =========================================================================
   binar.io — formatter workspace
   Theme-driven design. Dark is default; [data-theme="light"] overrides.
   ========================================================================= */

:root {
  /* sizing */
  --sidebar-expanded: 340px;
  --sidebar-collapsed: 48px;
  --nav-height: 56px;
  --banner-height: 52px;
  --radius: 6px;
  --radius-sm: 4px;

  /* brand */
  --accent: #22c55e;
  --accent-2: #16a34a;
  --accent-contrast: #04190f;
  --grid-line: rgba(255, 255, 255, 0.045);

  /* dark theme (default) */
  --bg: #0c0d10;
  --bg-grad-1: #14151a;
  --bg-grad-2: #0a0b0e;
  --panel: #15161b;
  --panel-2: #1b1d23;
  --panel-3: #23252d;
  --border: rgba(255, 255, 255, 0.09);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: rgba(255, 255, 255, 0.88);
  --text-dim: rgba(255, 255, 255, 0.6);
  --muted: rgba(255, 255, 255, 0.4);
  --shadow: 0 10px 30px rgba(0, 0, 0, 0.45);
  --shadow-sm: 0 2px 10px rgba(0, 0, 0, 0.3);
}

[data-theme="light"] {
  --bg: #f5f6f8;
  --bg-grad-1: #ffffff;
  --bg-grad-2: #eef0f4;
  --panel: #ffffff;
  --panel-2: #f3f4f7;
  --panel-3: #e9ebf0;
  --border: rgba(0, 0, 0, 0.1);
  --border-strong: rgba(0, 0, 0, 0.18);
  --text: #15171c;
  --text-dim: rgba(0, 0, 0, 0.62);
  --muted: rgba(0, 0, 0, 0.42);
  --accent-contrast: #04190f;
  --grid-line: rgba(0, 0, 0, 0.05);
  --shadow: 0 10px 30px rgba(20, 30, 50, 0.12);
  --shadow-sm: 0 2px 10px rgba(20, 30, 50, 0.08);
}

* {
  box-sizing: border-box;
}

html,
body {
  margin: 0;
  padding: 0;
  min-height: 100%;
}

body {
  font-family: "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  background: linear-gradient(160deg, var(--bg-grad-1), var(--bg-grad-2));
  background-attachment: fixed;
  color: var(--text);
  -webkit-font-smoothing: antialiased;
  transition: background 0.3s ease, color 0.3s ease;
  /* leave room for the pinned privacy banner */
  padding-bottom: var(--banner-height);
}

a {
  color: inherit;
  text-decoration: none;
}

.muted {
  color: var(--muted);
  font-size: 0.85rem;
}

/* ---------------------------------------------------------------- TOP NAV */
.topnav {
  position: sticky;
  top: 0;
  z-index: 50;
  height: var(--nav-height);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  background: color-mix(in srgb, var(--panel) 85%, transparent);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--border);
}

.nav-left {
  display: flex;
  align-items: center;
  gap: 6px;
}

.nav-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.logo {
  font-family: "JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace;
  font-weight: 700;
  font-size: 1.15rem;
  color: var(--text);
  margin-right: 18px;
  letter-spacing: -0.02em;
}
.logo:hover {
  color: var(--accent);
}

.topnav a:not(.logo),
.dropbtn {
  color: var(--text-dim);
  font-size: 0.92rem;
  padding: 7px 10px;
  border-radius: var(--radius-sm);
  transition: background 0.18s, color 0.18s;
  cursor: pointer;
}

.topnav a:not(.logo):hover,
.dropbtn:hover {
  background: var(--panel-2);
  color: var(--text);
}

/* dropdowns */
.dropdown {
  position: relative;
  display: inline-block;
}
.dropbtn {
  background: none;
  border: none;
  font-family: inherit;
}
/* invisible bridge so the menu doesn't collapse while the cursor crosses
   the gap between the button and the panel */
.dropdown::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 0;
  width: 200px;
  height: 10px;
  display: none;
}
.dropdown:hover::after {
  display: block;
}
.dropdown-content {
  display: none;
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 180px;
  background: var(--panel);
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
  border-radius: var(--radius-sm);
  overflow: hidden;
  padding: 6px;
}
.dropdown-content a {
  display: block;
  padding: 8px 12px;
  border-radius: 6px;
  color: var(--text-dim) !important;
  font-size: 0.9rem;
}
.dropdown-content a:hover {
  background: var(--panel-2);
  color: var(--text) !important;
}
.dropdown:hover .dropdown-content {
  display: block;
}

.theme-toggle {
  background: var(--panel-2);
  border: 1px solid var(--border);
  color: var(--text);
  width: 38px;
  height: 38px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1rem;
  display: grid;
  place-items: center;
  transition: transform 0.2s, border-color 0.2s, background 0.2s;
}
.theme-toggle:hover {
  border-color: var(--accent);
  color: var(--accent);
}
.theme-toggle svg {
  display: block;
}

/* ---------------------------------------------------------------- LAYOUT */
.app {
  display: flex;
  align-items: stretch;
  gap: 16px;
  padding: 16px;
  min-height: calc(100vh - var(--nav-height));
}

main {
  flex: 1;
  min-width: 0;
  color: var(--text);
}

/* ---------------------------------------------------------------- SIDEBARS */
.sidebar {
  position: relative;
  flex-shrink: 0;
  width: var(--sidebar-collapsed);
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
  transition: width 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.app.is-open-left .sidebar-left {
  width: var(--sidebar-expanded);
}

.sidebar-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}
/* keep left options from reflowing while the panel animates open */
.sidebar-left .sidebar-content {
  min-width: var(--sidebar-expanded);
}

/* panel header bar (title · keycap · collapse chevron) */
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 11px 12px;
  border-bottom: 1px solid var(--border);
}
.panel-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--text-dim);
  white-space: nowrap;
}
.panel-title .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 8px var(--accent);
}
.panel-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.icon-btn {
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  color: var(--text-dim);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.icon-btn:hover {
  background: var(--panel-3);
  color: var(--text);
}
.panel-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 12px;
}

/* ad placeholder pinned to the bottom of a sidebar */
.ad-wrap {
  flex-shrink: 0;
  margin: 8px 12px 12px;
}
.ad-caption {
  display: block;
  margin-bottom: 5px;
  font-size: 0.68rem;
  letter-spacing: 0.06em;
  color: var(--muted);
  text-align: center;
}
.ad-slot {
  min-height: 250px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 1px dashed var(--border-strong);
  border-radius: var(--radius-sm);
  background: var(--panel-2);
  color: var(--muted);
}
.ad-slot-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}
.ad-slot-size {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 0.7rem;
}

/* collapsed rail */
.rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  height: 100%;
  padding: 12px 0;
  background: transparent;
  border: none;
  color: var(--text-dim);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.rail:hover {
  background: var(--panel-2);
  color: var(--text);
}
.rail-label {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 0.7rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--muted);
}

/* right sidebar resize handle */
.sidebar-right .resize-handle {
  position: absolute;
  top: 0;
  left: 0;
  width: 6px;
  height: 100%;
  cursor: ew-resize;
  z-index: 10;
}
.sidebar-right .resize-handle:hover {
  background: color-mix(in srgb, var(--accent) 30%, transparent);
}

.struct-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px 4px;
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 0.72rem;
  color: var(--muted);
}
.struct-meta .copy-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: transparent;
  border: 1px solid transparent;
  color: var(--text-dim);
  padding: 3px 7px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-size: 0.72rem;
  font-family: inherit;
}
.struct-meta .copy-btn:hover {
  background: var(--panel-3);
  color: var(--text);
}
.struct-view {
  flex: 1;
  min-height: 0;
  margin: 6px 12px 12px;
  border-radius: var(--radius-sm);
  overflow: hidden;
  border: 1px solid var(--border);
}
.struct-view .muted {
  padding: 12px;
}

/* ---------------------------------------------------------- OPTIONS PANEL */
.options-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
  color: var(--text);
}

/* Each labeled subsection (e.g. "Output", "Struct View") */
.option-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.option-group-title {
  margin: 0 0 2px;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
  padding-bottom: 5px;
  border-bottom: 1px solid var(--border);
}
.options-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}
.options-header h3 {
  margin: 0;
  font-size: 1rem;
}
.kbd-hint {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 0.66rem;
  color: var(--muted);
  border: 1px solid var(--border);
  border-radius: 5px;
  padding: 2px 6px;
}

.option-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: var(--panel-2);
  border: 1px solid var(--border);
  padding: 9px 12px;
  border-radius: var(--radius-sm);
}
.option-field .opt-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.option-field label {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--text);
}
.option-field .opt-help {
  display: block;
  font-size: 0.72rem;
  font-weight: 400;
  color: var(--muted);
  margin-top: 2px;
}

/* number stepper */
.stepper {
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
  overflow: hidden;
  background: var(--panel);
}
.stepper button {
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  color: var(--text-dim);
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
}
.stepper button:hover {
  background: var(--panel-3);
  color: var(--text);
}
.stepper input {
  width: 36px;
  border: none;
  background: transparent;
  color: var(--text);
  text-align: center;
  font-size: 0.85rem;
  font-family: inherit;
  -moz-appearance: textfield;
}
.stepper input::-webkit-outer-spin-button,
.stepper input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.reset-defaults {
  margin-top: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  width: 100%;
  padding: 9px;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-dim);
  background: var(--panel-2);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.reset-defaults:hover {
  background: var(--panel-3);
  color: var(--text);
}
.option-field input[type="number"],
.option-field select {
  background: var(--panel);
  border: 1px solid var(--border-strong);
  color: var(--text);
  border-radius: 6px;
  padding: 5px 8px;
  font-size: 0.85rem;
  font-family: inherit;
}
.option-field input[type="number"] {
  width: 64px;
  text-align: right;
}
.option-field select {
  cursor: pointer;
}

/* toggle switch */
input.switch {
  appearance: none;
  -webkit-appearance: none;
  position: relative;
  width: 38px;
  height: 22px;
  border-radius: 22px;
  background: var(--panel-3);
  border: 1px solid var(--border-strong);
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;
}
input.switch::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}
input.switch:checked {
  background: var(--accent);
  border-color: var(--accent);
}
input.switch:checked::after {
  transform: translateX(16px);
}

/* ---------------------------------------------------------- FORMATTER PAGE */
.formatter-page {
  padding: 4px;
}
.workspace-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin: 4px 0 16px;
}
.title {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.01em;
}
.title .title-sep {
  color: var(--muted);
  font-weight: 500;
}
.title .title-kind {
  color: var(--text-dim);
  font-weight: 500;
}
.workspace-meta {
  font-size: 0.8rem;
  color: var(--muted);
  margin-top: 4px;
  font-family: "JetBrains Mono", ui-monospace, monospace;
}
.head-shortcuts {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 0.76rem;
  color: var(--text-dim);
}
.head-shortcuts .sc {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

/* UI chrome (labels + header tags) is not content — don't let it be highlighted.
   The editor bodies stay selectable; only the surrounding labels are locked. */
.btn-label,
.workspace-head,
.editor-header,
.editor-status,
.keycap {
  user-select: none;
  -webkit-user-select: none;
}

.formatter-flex {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: stretch;
}
/* single-pane (Formatter) — one editor spanning the full width */
.formatter-flex.single {
  grid-template-columns: 1fr;
}

.editor-box {
  display: flex;
  flex-direction: column;
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  height: calc(100vh - 218px);
  min-height: 420px;
}
.editor-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 9px 12px;
  background: var(--panel-2);
  border-bottom: 1px solid var(--border);
  font-size: 0.82rem;
  color: var(--text-dim);
}
.editor-head-left {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
  flex-shrink: 0;
}
.editor-title {
  font-weight: 700;
  color: var(--text);
}
.lang-badge {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 0.68rem;
  color: var(--text-dim);
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 1px 6px;
  white-space: nowrap;
}
.byte-count {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 0.7rem;
  color: var(--muted);
  white-space: nowrap;
}

.buttons {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

/* compact mode: collapse toolbar buttons to icon-only (gap handles spacing) */
.editor-header.compact .btn-label {
  display: none;
}
.editor-header.compact .tool-btn,
.editor-header.compact .primary {
  padding-left: 8px;
  padding-right: 8px;
}
button {
  font-family: inherit;
}
.tool-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: transparent;
  border: 1px solid transparent;
  color: var(--text-dim);
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 500;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s;
}
.tool-btn:hover {
  background: var(--panel-3);
  color: var(--text);
}
.buttons .primary {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: linear-gradient(180deg, var(--accent), var(--accent-2));
  border: 1px solid var(--accent-2);
  color: var(--accent-contrast);
  padding: 5px 14px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 700;
  white-space: nowrap;
  transition: filter 0.15s;
}
.buttons .primary:hover {
  filter: brightness(1.07);
}

.editor-body {
  flex: 1;
  min-height: 0;
}
.editor-status {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 12px;
  border-top: 1px solid var(--border);
  background: var(--panel-2);
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 0.68rem;
  color: var(--muted);
}
.editor-status .ready {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--text-dim);
}
.editor-status .ready::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 6px var(--accent);
}

/* --------------------------------------------------------- CONVERTER BAR */
.converter-bar {
  display: flex;
  align-items: flex-end;
  gap: 14px;
  margin: 0 0 14px;
}
.converter-bar label {
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
}
.converter-bar select {
  background: var(--panel);
  border: 1px solid var(--border-strong);
  color: var(--text);
  border-radius: var(--radius-sm);
  padding: 8px 12px;
  font-size: 0.9rem;
  font-family: inherit;
  cursor: pointer;
  min-width: 150px;
}
.converter-arrow {
  color: var(--accent);
  font-size: 1.2rem;
  padding-bottom: 8px;
}

/* ------------------------------------------------------- TOOL SEO CONTENT */
.tool-content {
  max-width: 820px;
  margin: 28px auto 8px;
  padding: 24px 4px 8px;
  border-top: 1px solid var(--border);
  color: var(--text-dim);
  line-height: 1.6;
}
.tool-content h2 {
  font-size: 1.3rem;
  color: var(--text);
  margin: 0 0 12px;
}
.tool-content h3 {
  font-size: 1rem;
  color: var(--text);
  margin: 24px 0 10px;
}
.tool-content p {
  margin: 0 0 12px;
}
.tool-content .tc-features {
  margin: 0;
  padding-left: 18px;
}
.tool-content .tc-features li {
  margin-bottom: 6px;
}
.tool-content .tc-faq dt {
  font-weight: 600;
  color: var(--text);
  margin-top: 12px;
}
.tool-content .tc-faq dd {
  margin: 4px 0 0;
}
.tool-content .tc-related {
  list-style: none;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
}
.tool-content .tc-related a {
  color: var(--accent);
}
.tool-content .tc-related a:hover {
  text-decoration: underline;
}

/* ---------------------------------------------------------------- HOME */
.home {
  position: relative;
  max-width: 1080px;
  margin: 0 auto;
  padding: 64px 20px;
}
/* subtle, theme-aware grid behind the hero (full-bleed, no scrollbar) */
.home-grid {
  position: fixed;
  inset: var(--nav-height) 0 0 0;
  z-index: 0;
  pointer-events: none;
  background-image: linear-gradient(var(--grid-line) 1px, transparent 1px),
    linear-gradient(90deg, var(--grid-line) 1px, transparent 1px);
  background-size: 44px 44px;
  -webkit-mask-image: radial-gradient(
    ellipse 64% 50% at 50% 22%,
    #000 30%,
    transparent 100%
  );
  mask-image: radial-gradient(
    ellipse 64% 50% at 50% 22%,
    #000 30%,
    transparent 100%
  );
}
.home > * {
  position: relative;
  z-index: 1;
}
.home .intro {
  text-align: center;
  margin-bottom: 56px;
}
.version-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 0.78rem;
  color: var(--text-dim);
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 5px 14px;
  margin-bottom: 26px;
}
.version-pill .dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 8px var(--accent);
}
.hero-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 32px;
}
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.92rem;
  font-weight: 600;
  padding: 11px 20px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--border-strong);
  background: var(--panel-2);
  color: var(--text);
  cursor: pointer;
  transition: filter 0.15s, background 0.15s, border-color 0.15s;
}
.btn:hover {
  background: var(--panel-3);
}
.btn.btn-primary {
  background: linear-gradient(180deg, var(--accent), var(--accent-2));
  border-color: var(--accent-2);
  color: var(--accent-contrast);
}
.btn.btn-primary:hover {
  filter: brightness(1.07);
}
.hero-shortcuts {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 18px;
  margin-top: 28px;
  font-size: 0.82rem;
  color: var(--text-dim);
}
.hero-shortcuts .sc {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

/* keycap chips (hero + workspace header) */
.keycap {
  display: inline-grid;
  place-items: center;
  min-width: 22px;
  height: 22px;
  padding: 0 5px;
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 0.72rem;
  color: var(--text);
  background: var(--panel-2);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
}
.keycap-group {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.home .intro h1 {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 2.6rem;
  font-weight: 700;
  margin: 0 0 14px;
  letter-spacing: -0.02em;
}
.home .intro p {
  font-size: 1.12rem;
  line-height: 1.6;
  color: var(--text-dim);
  max-width: 620px;
  margin: 0 auto;
}
.cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
}
.card {
  display: flex;
  flex-direction: column;
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 28px;
  box-shadow: var(--shadow-sm);
  transition: transform 0.2s, border-color 0.2s, box-shadow 0.2s;
}
.card:hover {
  transform: translateY(-4px);
  border-color: var(--accent);
  box-shadow: var(--shadow);
}
.card h2 {
  font-size: 1.3rem;
  margin: 0 0 10px;
}
.card p {
  flex: 1;
  font-size: 0.95rem;
  line-height: 1.55;
  color: var(--text-dim);
  margin: 0 0 18px;
}
.card .cta {
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--accent);
}

/* ---------------------------------------------------------------- PROSE */
.page-prose {
  max-width: 720px;
  margin: 0 auto;
  padding: 48px 20px;
  line-height: 1.65;
}
.page-prose h1 {
  font-size: 2rem;
  margin-bottom: 16px;
}
.page-prose h2 {
  font-size: 1.25rem;
  margin-top: 32px;
}
.page-prose p {
  color: var(--text-dim);
}
.page-prose ul {
  color: var(--text-dim);
  line-height: 1.6;
}
.page-prose ul li {
  margin-bottom: 8px;
}
.page-prose .pledge {
  margin: 24px 0;
  padding: 16px 20px;
  border-left: 3px solid var(--accent);
  border-radius: var(--radius-sm);
  background: color-mix(in srgb, var(--accent) 9%, transparent);
  color: var(--text);
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.55;
}
.shortcut-list {
  list-style: none;
  padding: 0;
}
.shortcut-list li {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  color: var(--text-dim);
}
.kbd {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 0.78rem;
  background: var(--panel-2);
  border: 1px solid var(--border-strong);
  border-bottom-width: 2px;
  border-radius: 6px;
  padding: 3px 8px;
  color: var(--text);
  min-width: 96px;
  text-align: center;
}

/* ---------------------------------------------------------------- FOOTER */
.site-footer {
  background: var(--panel);
  border-top: 1px solid var(--border);
  padding: 40px 32px 28px;
  font-size: 0.82rem;
}
.footer-top {
  display: grid;
  grid-template-columns: 1.6fr 1fr 1fr 1fr;
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
}
.footer-brand .logo {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-weight: 700;
  font-size: 1rem;
  color: var(--text);
}
.footer-brand p {
  margin: 12px 0 0;
  color: var(--text-dim);
  line-height: 1.5;
  max-width: 280px;
}
.footer-col h3 {
  margin: 0 0 12px;
  font-size: 0.74rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}
.footer-col a {
  display: block;
  color: var(--text-dim);
  margin-bottom: 8px;
  transition: color 0.15s;
}
.footer-col a:hover {
  color: var(--accent);
}
.footer-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  max-width: 1200px;
  margin: 32px auto 0;
  padding-top: 20px;
  border-top: 1px solid var(--border);
  color: var(--muted);
}
.footer-tags {
  font-family: "JetBrains Mono", ui-monospace, monospace;
}

/* -------------------------------------------------------- PRIVACY BANNER */
.privacy-banner {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 60;
  min-height: var(--banner-height);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 20px;
  background: color-mix(in srgb, var(--panel) 92%, transparent);
  backdrop-filter: blur(12px);
  border-top: 1px solid var(--accent);
  box-shadow: 0 -6px 20px rgba(0, 0, 0, 0.25);
}
.privacy-banner p {
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.4;
  color: var(--text-dim);
}
.privacy-banner strong {
  color: var(--accent);
  font-weight: 700;
}
.privacy-icon {
  flex-shrink: 0;
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: color-mix(in srgb, var(--accent) 16%, transparent);
  color: var(--accent);
}

/* ---------------------------------------------------------------- MOBILE */
@media (max-width: 820px) {
  .formatter-flex {
    grid-template-columns: 1fr;
  }
  :root {
    --banner-height: 76px;
  }
  .privacy-banner p {
    font-size: 0.76rem;
  }
}
